import {
  BarChart as RechartsBar,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { ChartCard } from './ChartCard';
import { CHART_COLORS, axisTick, useChartLocale } from './chartTheme';

const truncate = (value: unknown, max = 18) => {
  const str = String(value ?? '');
  return str.length > max ? `${str.slice(0, max - 1)}…` : str;
};

interface BarChartProps {
  data: { name: string; value: number; color?: string }[];
  title?: string;
  height?: number;
  horizontal?: boolean;
  color?: string;
  /** Label for the value in the tooltip (defaults to the generic "Total"). */
  valueLabel?: string;
  emptyMessage?: string;
  className?: string;
}

export function BarChart({
  data,
  title,
  height = 300,
  horizontal = false,
  color = CHART_COLORS.primary,
  valueLabel,
  emptyMessage,
  className,
}: BarChartProps) {
  const { t } = useTranslation();
  const { isRtl, formatNumber, tooltip } = useChartLocale();
  const isEmpty = data.length === 0;

  // Rounded corners on the bar's far end, mirrored in RTL.
  const radius: [number, number, number, number] = horizontal
    ? isRtl
      ? [4, 0, 0, 4]
      : [0, 4, 4, 0]
    : [4, 4, 0, 0];

  return (
    <ChartCard title={title} isEmpty={isEmpty} emptyMessage={emptyMessage} className={className}>
      <div dir="ltr">
        <ResponsiveContainer width="100%" height={height}>
          <RechartsBar
            data={data}
            layout={horizontal ? 'vertical' : 'horizontal'}
            margin={{ top: 8, right: 8, left: 8, bottom: 0 }}
          >
            <CartesianGrid
              stroke={CHART_COLORS.grid}
              strokeDasharray="3 3"
              vertical={horizontal}
              horizontal={!horizontal}
            />
            {horizontal ? (
              <>
                <XAxis
                  type="number"
                  tick={axisTick}
                  tickLine={false}
                  axisLine={false}
                  reversed={isRtl}
                  tickFormatter={formatNumber}
                  allowDecimals={false}
                />
                <YAxis
                  dataKey="name"
                  type="category"
                  tick={axisTick}
                  tickLine={false}
                  axisLine={false}
                  width={120}
                  orientation={isRtl ? 'right' : 'left'}
                  tickFormatter={(v) => truncate(v)}
                  interval={0}
                />
              </>
            ) : (
              <>
                <XAxis
                  dataKey="name"
                  tick={axisTick}
                  tickLine={false}
                  axisLine={{ stroke: CHART_COLORS.grid }}
                  reversed={isRtl}
                  tickFormatter={(v) => truncate(v, 12)}
                />
                <YAxis
                  tick={axisTick}
                  tickLine={false}
                  axisLine={false}
                  width={44}
                  orientation={isRtl ? 'right' : 'left'}
                  tickFormatter={formatNumber}
                  allowDecimals={false}
                />
              </>
            )}
            <Tooltip {...tooltip} formatter={(value) => [formatNumber(value), valueLabel ?? t('charts.total')]} />
            <Bar dataKey="value" fill={color} radius={radius} maxBarSize={horizontal ? 24 : 48}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color || color} />
              ))}
            </Bar>
          </RechartsBar>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}

// Stacked bar chart for comparison
interface StackedBarChartProps {
  data: Record<string, string | number>[];
  keys: { key: string; name: string; color: string }[];
  xAxisKey: string;
  title?: string;
  height?: number;
  /** Optional formatter for x-axis tick and tooltip labels. */
  labelFormatter?: (value: unknown) => string;
  emptyMessage?: string;
  className?: string;
}

export function StackedBarChart({
  data,
  keys,
  xAxisKey,
  title,
  height = 300,
  labelFormatter,
  emptyMessage,
  className,
}: StackedBarChartProps) {
  const { isRtl, formatNumber, tooltip, legendStyle } = useChartLocale();
  const format = labelFormatter ?? ((v: unknown) => String(v ?? ''));

  return (
    <ChartCard title={title} isEmpty={data.length === 0} emptyMessage={emptyMessage} className={className}>
      <div dir="ltr">
        <ResponsiveContainer width="100%" height={height}>
          <RechartsBar data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey={xAxisKey}
              tick={axisTick}
              tickLine={false}
              axisLine={{ stroke: CHART_COLORS.grid }}
              reversed={isRtl}
              tickFormatter={format}
            />
            <YAxis
              tick={axisTick}
              tickLine={false}
              axisLine={false}
              width={44}
              orientation={isRtl ? 'right' : 'left'}
              tickFormatter={formatNumber}
              allowDecimals={false}
            />
            <Tooltip {...tooltip} labelFormatter={format} formatter={(value) => formatNumber(value)} />
            <Legend iconType="circle" iconSize={8} wrapperStyle={legendStyle} />
            {keys.map((k, i) => (
              <Bar
                key={k.key}
                dataKey={k.key}
                name={k.name}
                stackId="a"
                fill={k.color}
                maxBarSize={48}
                radius={i === keys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
              />
            ))}
          </RechartsBar>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}
